import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { Guid } from "@/Common/Models/Guid";
import { InboundFaxesUIStateInbox, InboundFaxSortOrderType } from "@/Common/Models/UI/Enums";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { CommunicationsTab_OptimizedV2 } from "@/Communications/Components/CommunicationsTab_OptimizedV2";
import { InboundFaxesMailboxActions } from "@/Documents/Components/InboundFax/InboundFaxesMailboxActions";
import { InboundFaxMailboxFilterSettings } from "@/Documents/Components/InboundFax/InboundFaxMailboxFilterSettings";
import { InboundFaxNotesPopup } from "@/Documents/Components/InboundFax/InboundFaxNotesPopup";
import { InboundFax } from "@/Documents/Models/InboundFax/InboundFax";
import { InboundFaxMailboxModel } from "@/Documents/Models/InboundFax/InboundFaxMailboxModel";
import { InboundFaxNotes } from "@/Documents/Models/InboundFax/InboundFaxNotes";
import { InboundFaxMailboxFilterSettingsModel } from "@/Documents/Models/Settings/InboundFaxMailboxFilterSettings";

interface IInboundFaxMailboxProps {
    mailboxModel: InboundFaxMailboxModel;
    inboxType: InboundFaxesUIStateInbox;
    onFaxClick: (currentFax: InboundFax) => void;
    onRefreshFaxList: (then?: () => void) => void;
    onShowReturnFaxToSenderPopup: (faxes: InboundFax[]) => void;
    mailboxSettingsAreVisible: boolean;
    onCloseMailboxSettings: () => void;
}

class InboundFaxMailboxState {
    public tableRowsData: TableDataSourceItem<InboundFax>[];
    public isColumnOrderingPopupVisible: boolean = false;
    public showInboundFaxNotesPopup: boolean;
    public selectedRowFaxItem;
}

class InboundFaxesMailbox extends BaseReactComponent<IInboundFaxMailboxProps, InboundFaxMailboxState> {
    private saveOptionKey: string = "FaxInbox";
    public constructor(props: IInboundFaxMailboxProps) {
        super(props);
        this.state = { tableRowsData: [], isColumnOrderingPopupVisible: false, showInboundFaxNotesPopup: false, selectedRowFaxItem: null };
    }

    public componentDidMount(): void {
        this.mutateState(s => s.tableRowsData = this.props.mailboxModel.faxes?.map(fax => new TableDataSourceItem<InboundFax>(fax)) ?? []);
        InboundFaxesMailboxActions.updateInboundFaxUnmanagedFaxCount(documentsStore.InboundFaxInboxModel.filteringSettings);
    }

    public componentDidUpdate(prevProps: IInboundFaxMailboxProps): void {
        if (prevProps.mailboxModel.faxes !== this.props.mailboxModel.faxes)
            this.mutateState(s => s.tableRowsData = this.props.mailboxModel.faxes?.map(fax => new TableDataSourceItem<InboundFax>(fax)) ?? []);
    }

    public render(): JSX.Element {
        const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const columnOptionsForOrderColumnsPopup = new MyleHelper().castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div>
                {this.props.mailboxSettingsAreVisible && (
                    <InboundFaxMailboxFilterSettings
                        onSaveChanges={(settings, isSaveSettingsChecked) => this.saveFilterSettings(settings, isSaveSettingsChecked)}
                        settings={this.props.mailboxModel.filteringSettings}
                        onClose={() => this.props.onCloseMailboxSettings()}
                        onColumnOrderingPopupOpenClick={() => (
                            this.mutateState(
                                s => s.isColumnOrderingPopupVisible = true,
                                () => this.props.onCloseMailboxSettings()
                            )
                        )}
                    />
                )}

                {this.state.isColumnOrderingPopupVisible && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnOptionsForOrderColumnsPopup}
                        SaveOptionskeyTarget={this.saveOptionKey}
                        ContainerId="inboundFaxColumnSettingsPopup"
                        closeColumnOrderPopup={() => this.mutateState(s => s.isColumnOrderingPopupVisible = false)}
                    />
                )}

                <div id="posref" className="ws-scrollable">
                    <div className="myle-doc-tb main-content myle-form">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="document_faxes_table"
                            TableDataCy="document-faxes-table"
                            Columns={this.getTableColumns(columnsOptions)}
                            DataSource={this.state.tableRowsData.toImmutable()}
                            // TODO: Settings are going to be implemented in another task
                            // ColumnsPreferencesChangedOperation={Myle.Operations.Server.Myle_UpdateDocumentFaxSettings}
                            SaveOptionskeyTarget={this.saveOptionKey}
                            ColumnReorderTarget={MyleTableTarget.Myle_Documents_InboundFaxes_ColumnReorder}
                            ColumnSortTarget={MyleTableTarget.Myle_Documents_InboundFaxes_ColumnSortClick}
                            RowClickFunction={(dataSourceItem: TableDataSourceItem<InboundFax>) => this.props.onFaxClick(dataSourceItem.Model)}
                        />
                    </div>
                    {this.state.showInboundFaxNotesPopup && this.state.selectedRowFaxItem != null && (
                        <InboundFaxNotesPopup
                            onAccept={(faxId, note) => this.addFaxNote(faxId, note)}
                            onCancel={() => { this.mutateState(s => { s.showInboundFaxNotesPopup = false; s.selectedRowFaxItem = null; }); }}
                            faxId={this.state.selectedRowFaxItem.Model.Id}
                            existingFaxNotes={this.state.selectedRowFaxItem.Model.FaxNotes}
                        />
                    )}
                </div>

                <div className={CommunicationsTab_OptimizedV2.footerCSS}>
                    <div className="col-lg-6 col-md-6 col-sm-6">
                        <SimplePagerControl
                            PageSize={this.props.mailboxModel.filteringSettings.QueryPageSize}
                            TotalRows={this.props.mailboxModel.queryRowCount}
                            CurrentPage={this.props.mailboxModel.queryCurrentPageNumber}
                            changePageFunction={pageNumber => InboundFaxesMailboxActions.changeQueryPageNumber(pageNumber)}
                        />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-6 text-right">
                        {this.props.inboxType === InboundFaxesUIStateInbox.Inbox &&
                            this.buildInboxButtons()
                        }
                        {this.props.inboxType === InboundFaxesUIStateInbox.Trash &&
                            this.buildTrashButtons()
                        }
                    </div>
                </div>
            </div>
        );
    }

    private buildInboxButtons(): JSX.Element {
        return (
            <div>
                <ButtonControl
                    onClick={() => this.displayDeletePopup()}
                    label={cacheStore.GetString("DeleteLabel")}
                    className="btn btn-danger push-right-5"
                    disabled={!this.getSelectedFaxes().any()}
                />
                <ButtonControl
                    onClick={() => this.props.onShowReturnFaxToSenderPopup(this.getSelectedFaxes())}
                    label={cacheStore.GetString("ReturnToSenderLabel")}
                    className="btn btn-primary"
                    disabled={this.getSelectedFaxes().map(p => p.SenderFaxNumber).distinct().length != 1}
                />
            </div>
        );
    }

    private buildTrashButtons(): JSX.Element {
        return (
            <div>
                <ButtonControl
                    onClick={() => this.showPermanentlyDeleteFaxPopup()}
                    label={cacheStore.GetString("DeleteLabel")}
                    className="btn btn-danger push-right-5"
                    disabled={!this.getSelectedFaxes().any()}
                />
                <ButtonControl
                    onClick={() => this.showRestoreFaxPopup()}
                    label={cacheStore.GetString("RestoreLabel")}
                    className="btn btn-primary"
                    disabled={!this.getSelectedFaxes().any()}
                />
            </div>
        );
    }

    private displayDeletePopup(): void {
        sessionStore.showPopup({
            title: cacheStore.GetString("WarningLabel"),
            content: cacheStore.GetString("AreYouSureWantToDeleteThisFile"),
            okLabel: cacheStore.GetString("DeleteLabel"),
            showCancel: true,
            okVariant: "error",
            showCloseX: true,
            okAction: () => this.deleteFaxes()
        });
    }

    private getSelectedFaxes(): InboundFax[] {
        return this.state.tableRowsData.filter(tableItem => tableItem.IsSelected).map(tableItem => tableItem.Model);
    }

    private getSelectedFaxIds(): string[] {
        return this.getSelectedFaxes().map(fax => fax.Id);
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        const defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<TableDataSourceItem<InboundFax>>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Selection":
                        return {
                            renderCell: item => this.renderSelectFaxCheckboxCell(item),
                            renderHeader: () => this.renderSelectAllFaxesCheckboxCell()
                        };

                    case "Action":
                        return {
                            renderCell: item => this.renderActionColumn(item)
                        };

                    case "Triage":
                        return {
                            renderCell: item => this.renderTriageColumn(item)
                        };

                    default:
                        return {
                            renderCell: item => this.renderTextCell(column.FieldName, item)
                        };
                }
            },
            false
        );
    }

    private renderTextCell(fieldName: string, item: TableDataSourceItem<InboundFax>): JSX.Element {
        const text = this.formatCellText(fieldName, item);

        return <div className={!item.Model.ReadDate ? "font-weight-bold" : ""}>{text}</div>;
    }

    private formatCellText(fieldName: string, item: TableDataSourceItem<InboundFax>): string {
        switch (fieldName) {
            case "Pages":
                return this.getPertinentPageCount(item).toString();
            case "DateReceived":
                return this.formatDate(item).toString();
            case "ClassName":
                return this.getClassName(item);
            case "SenderFaxNumber":
                return (item.Model[fieldName] && item.Model[fieldName].trim() !== "")
                    ? MyleHelper.FormatPhone(MyleHelper.RemoveCountryCodeCanadianE164PhoneNumber(item.Model[fieldName]), "CNCANADA")
                    : "";
            default:
                return item.Model[fieldName];
        }
    }

    private renderSelectFaxCheckboxCell(item: TableDataSourceItem<InboundFax>): JSX.Element {
        return (
            <CheckboxControl
                id={item.Model.Id}
                checked={item.IsSelected}
                onChange={() => this.selectFax(item)}
            />
        );
    }

    private renderSelectAllFaxesCheckboxCell(): JSX.Element {
        // the enclosing div is actually needed for alignment in a legacy component.
        return (
            <div>
                <CheckboxControl
                    checked={this.getAreAllFaxesChecked()}
                    onChange={checked => this.selectAllFaxes(checked)}
                />
            </div>
        );
    }

    private formatDate(item: TableDataSourceItem<InboundFax>): string {
        return LuxonDateTimeParser
            .parse(item.Model.DateReceived)
            .toLocal()
            .toFormat(`yyyy/LL/dd '${cacheStore.GetString("AtLabel")}' HH:mm:ss`);
    }

    private getPertinentPageCount(item: TableDataSourceItem<InboundFax>): number {
        switch (this.props.inboxType) {
            case InboundFaxesUIStateInbox.Inbox:
                return item.Model?.PendingPageCount ?? 0;
            case InboundFaxesUIStateInbox.Trash:
                return item.Model?.DeletedPageCount ?? 0;
            case InboundFaxesUIStateInbox.Indexed:
                return item.Model?.AssignedPageCount ?? 0;
            default:
                return 0;
        }
    }

    private getAreAllFaxesChecked(): boolean {
        return !this.state.tableRowsData.any(fax => !fax.IsSelected);
    }

    private getClassName(item: TableDataSourceItem<InboundFax>): string {
        const classLookupId = InboundFax.getClassLookupId(item.Model);
        if (classLookupId) {
            const documentClassString = cacheStore.GetLookupById(classLookupId.toString()).GetValue();
            return documentClassString;
        }

        return "";
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        const inboundFaxDefaultColumns = [
            new MyleTableColumnOptions({
                ...this.getCommonTableColumnOptions("", "Selection", null, 3.3),
                IsFixedInPlace: true,
                RowClickEnabled: false,
                IsSortable: false
            }),
            this.getCommonTableColumnOptions(cacheStore.GetString("DateReceivedLabel"), "DateReceived", InboundFaxSortOrderType.DateReceived, 15),
            this.getCommonTableColumnOptions(cacheStore.GetString("FaxNumberLabel"), "SenderFaxNumber", InboundFaxSortOrderType.FaxNumber, 20),
            this.getCommonTableColumnOptions(cacheStore.GetString("SourceLabel"), "Source", InboundFaxSortOrderType.Source, 10),
            this.getCommonTableColumnOptions(cacheStore.GetString("ClassLabel"), "ClassName", InboundFaxSortOrderType.Class, 10),
            this.getCommonTableColumnOptions(cacheStore.GetString("PagesLabel"), "Pages", this.getCurrentPageCountSortType(), 10),
            this.getCommonTableColumnOptions(cacheStore.GetString("ActionLabel"), "Action", InboundFaxSortOrderType.Action, 10)
        ];

        if (this.props.inboxType === InboundFaxesUIStateInbox.Inbox && cacheStore.ClinicSettings.EnableInboundFaxTriage) {
            inboundFaxDefaultColumns.push(this.getCommonTableColumnOptions(cacheStore.GetString("TriageLabel"), "Triage", InboundFaxSortOrderType.Triage, 10));
        }

        return inboundFaxDefaultColumns;
    }

    private getCommonTableColumnOptions(headerText: string, fieldName: string, sortColumn: InboundFaxSortOrderType, WidthPercentage: number): MyleTableColumnOptions {
        return new MyleTableColumnOptions({
            HeaderText: headerText,
            FieldName: fieldName,
            RowClickEnabled: true,
            IsSortable: true,
            SortColumn: sortColumn,
            SortDirection: this.props.mailboxModel.queryOrderDirection,
            IsSortActive: this.props.mailboxModel.queryOrderType === sortColumn,
            WidthPercentageDefault: WidthPercentage,
            sortingMethod: () => this.sortFaxList(sortColumn)
        });
    }

    private getCurrentPageCountSortType(): InboundFaxSortOrderType {
        return this.props.inboxType === InboundFaxesUIStateInbox.Trash
            ? InboundFaxSortOrderType.DeletedPagesCount
            : InboundFaxSortOrderType.PendingPagesCount;
    }

    private showRestoreFaxPopup(): void {
        sessionStore.showPopup({
            title: cacheStore.GetString("WarningLabel"),
            content: cacheStore.GetString("AreYouSureYouWantToRestoreThisFileLabel"),
            showCancel: true,
            showCloseX: true,
            okAction: () => this.restoreSelectedFaxes()
        });
    }

    private async restoreSelectedFaxes(): Promise<void> {
        const faxIds = this.getSelectedFaxIds();
        await InboundFaxesMailboxActions.restoreFaxes(faxIds);
        await InboundFaxesMailboxActions.updateInboundFaxUnmanagedFaxCount(this.props.mailboxModel.filteringSettings);
        this.props.onRefreshFaxList();
    }

    private selectAllFaxes(areAllFaxesSelected: boolean): void {
        this.mutateState(s => s.tableRowsData.forEach(c => c.IsSelected = areAllFaxesSelected));
    }

    private selectFax(item: TableDataSourceItem<InboundFax>): void {
        this.mutateState(s => s.tableRowsData.mutate(x => x.IsSelected = !item.IsSelected, x => item.Model.Id === x.Model.Id));
    }

    private showPermanentlyDeleteFaxPopup(): void {
        const selectionHasPendingPages = this.state.tableRowsData.any(p => p.IsSelected && p.Model.PendingPageCount > 0);

        if (selectionHasPendingPages) {
            sessionStore.showPopup(cacheStore.GetString("PermanentlyDeleteWithPendingPagesErrorLabel"));
            return;
        }

        sessionStore.showPopup({
            title: cacheStore.GetString("WarningLabel"),
            content: cacheStore.GetString("AreYouSureYouWantToPermanentlyDeleteThisFileLabel"),
            showCancel: true,
            showCloseX: true,
            okVariant: "error",
            okLabel: cacheStore.GetString("DeleteLabel"),
            okAction: () => {
                this.permanentlyDeleteSelectedFaxes();
            }
        });
    }

    private async permanentlyDeleteSelectedFaxes(): Promise<void> {
        const faxIds = this.getSelectedFaxIds();

        await InboundFaxesMailboxActions.permanentlyDeleteFaxes(faxIds);
        this.props.onRefreshFaxList();
    }

    private async deleteFaxes(): Promise<void> {
        const faxIds = this.getSelectedFaxIds();
        await InboundFaxesMailboxActions.deleteFaxes(faxIds);
        await InboundFaxesMailboxActions.updateInboundFaxUnmanagedFaxCount(documentsStore.InboundFaxInboxModel.filteringSettings);
        this.props.onRefreshFaxList();
    }

    private async sortFaxList(sortingColumn: InboundFaxSortOrderType): Promise<void> {
        const nextDirection = MyleTableHelper.getNewSortDir(sortingColumn, this.props.mailboxModel.queryOrderType, this.props.mailboxModel.queryOrderDirection);
        InboundFaxesMailboxActions.sortFaxList(sortingColumn, nextDirection);
    }

    private saveFilterSettings(settings: InboundFaxMailboxFilterSettingsModel, isSaveSettingsChecked: boolean): void {
        InboundFaxesMailboxActions.saveFilterSettings(settings, isSaveSettingsChecked);
        this.props.onCloseMailboxSettings();
    }

    // Action
    private renderActionColumn(item: TableDataSourceItem<InboundFax>): JSX.Element {
        const notesCount = item.Model.FaxNotes?.length ?? 0;
        const notesIndicatiorText = ` ${notesCount || "+"} `;
        const notesIndicatorCss = `bold-text${(item.Model.IsUrgent ? " red" : "")}`;
        return (
            <div id={item.Model.Id} className={item.Model.IsUrgent ? "fax-actions red-row" : " fax-actions "}>
                <a
                    aria-expanded="false"
                    onClick={e => this.openNotes(item, e)}
                >
                    [<span id={"dmt_" + item.Model.Id} className={notesIndicatorCss}>{notesIndicatiorText}</span>]
                </a>
            </div>
        );
    }

    private renderTriageColumn(item: TableDataSourceItem<InboundFax>): JSX.Element {
        if (this.props.inboxType === InboundFaxesUIStateInbox.Inbox && cacheStore.ClinicSettings.EnableInboundFaxTriage) {
            return (
                <a
                    onClick={e => this.onTriageClick(e, item.Model)}
                    style={{ paddingLeft: "5px" }}
                >
                    {cacheStore.GetString("TriageLabel")}
                </a>
            );
        }

        return null;
    }

    private openNotes(item: TableDataSourceItem<InboundFax>, e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        this.mutateState(s => {
            s.showInboundFaxNotesPopup = true;
            s.selectedRowFaxItem = item;
        });
    }

    private async addFaxNote(faxId: Guid, note: string): Promise<void> {
        const response: any = await InboundFaxesMailboxActions.addFaxNote(faxId, note);

        if (response.Warnings?.any()) {
            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: response.Warnings.firstOrDefault(),
                showCloseX: true,
                okAction: () => document.getElementById("faxNotesPopup_NotesField").focus()
            });
        }
        else {
            this.mutateState(
                s => {
                    s.showInboundFaxNotesPopup = false;
                    s.selectedRowFaxItem = null;
                    const currentIndex = s.tableRowsData.findIndex(x => x.Model.Id == faxId);
                    if (currentIndex >= 0) {
                        const faxNote: InboundFaxNotes = new InboundFaxNotes();
                        faxNote.Id = response.InboundFaxNote.Id;
                        faxNote.InboundFaxId = faxId;
                        faxNote.Notes = response.InboundFaxNote.Notes;
                        faxNote.CreatedByEmployeeId = response.InboundFaxNote.CreatedByEmployeeId;
                        faxNote.CreatedDate = response.InboundFaxNote.CreatedDate;
                        s.tableRowsData[currentIndex].Model.FaxNotes.push(faxNote);
                    }
                }
            );
        }
    }

    private onTriageClick(e: React.MouseEvent, fax: InboundFax): void {
        e.stopPropagation();
        InboundFaxesMailboxActions.showFaxTriageForm(fax);
    }
}
export { InboundFaxesMailbox };
